import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import stageIcon from "../../Assets/Icons/trophy.png";
import LoaderBox from "../HomePage/LoaderBox";
import AdminChampionshipBracket from "./AdminChampionshipBracket";

const progressionMap = {
  1: { nextSlot: 9, side: "t1" },
  2: { nextSlot: 9, side: "t2" },
  3: { nextSlot: 10, side: "t1" },
  4: { nextSlot: 10, side: "t2" },
  5: { nextSlot: 11, side: "t1" },
  6: { nextSlot: 11, side: "t2" },
  7: { nextSlot: 12, side: "t1" },
  8: { nextSlot: 12, side: "t2" },
  9: { nextSlot: 13, side: "t1" },
  10: { nextSlot: 13, side: "t2" },
  11: { nextSlot: 14, side: "t1" },
  12: { nextSlot: 14, side: "t2" },
  13: { nextSlot: 15, side: "t1" },
  14: { nextSlot: 15, side: "t2" },
};

const AdminChampionship = () => {
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // slotData nam sada služi samo za prikaz ID-ova u bracketingu, maknuli smo time/day
  const slotData = [
    { id: 1 },
    { id: 2 },
    { id: 3 },
    { id: 4 },
    { id: 5 },
    { id: 6 },
    { id: 7 },
    { id: 8 },
    /*   { id: 16 }, */
  ];

  const fetchInitialData = useCallback(async () => {
    setLoading(true);

    // 1. Dohvati sve timove i SVE odigrane utakmice
    const { data: teamsData } = await supabase.from("teams").select("*");
    const { data: matchesData } = await supabase
      .from("matches")
      .select("*")
      .eq("is_completed", true);

    // 2. Izračunaj statistiku (kao u HomeGroups)
    const stats = {};
    teamsData.forEach((t) => {
      stats[t.id] = { ...t, points: 0, goals_for: 0, goals_against: 0, gd: 0 };
    });

    matchesData.forEach((m) => {
      const tA = stats[m.team_a_id];
      const tB = stats[m.team_b_id];
      if (tA && tB) {
        tA.goals_for += m.scorea;
        tA.goals_against += m.scoreb;
        tB.goals_for += m.scoreb;
        tB.goals_against += m.scorea;
        if (m.scorea > m.scoreb) tA.points += 3;
        else if (m.scorea < m.scoreb) tB.points += 3;
        else {
          tA.points += 1;
          tB.points += 1;
        }
      }
    });

    // 3. Pretvori u niz i izračunaj gd
    const processedTeams = Object.values(stats).map((t) => ({
      ...t,
      gd: t.goals_for - t.goals_against,
    }));

    // 4. GRUPIRANJE I SORTIRANJE (Ovdje slažemo po tvojim pravilima!)
    const grouped = processedTeams.reduce((acc, team) => {
      acc[team.group_name] = acc[team.group_name] || [];
      acc[team.group_name].push(team);
      return acc;
    }, {});

    const topTeams = [];
    const groups = ["A", "B", "C", "D", "E", "F", "G", "H"];

    groups.forEach((gName) => {
      let gTeams = grouped[gName] || [];

      // SORTIRANJE ISTO KAO U HOMEGROUPS:
      gTeams.sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.gd !== a.gd) return b.gd - a.gd;
        if (b.goals_for !== a.goals_for) return b.goals_for - a.goals_for;
        return a.name.localeCompare(b.name, "hr");
      });

      // Uzmi prva 4
      gTeams.slice(0, 4).forEach((team, index) => {
        topTeams.push({ ...team, bracketLabel: `${gName}${index + 1}` });
      });
    });

    const { data: matchData } = await supabase
      .from("matches")
      .select(`*, team_a:team_a_id(name), team_b:team_b_id(name)`)
      .eq("group_name", "Završnica");

    setFilteredTeams(topTeams);
    setMatches(matchData || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const updateNextRound = useCallback(
    async (nextSlot, side, winnerId) => {
      const existing = matches.find((m) => m.bracket_slot_id === nextSlot);
      const payload =
        side === "t1" ? { team_a_id: winnerId } : { team_b_id: winnerId };

      const currentId =
        side === "t1" ? existing?.team_a_id : existing?.team_b_id;
      if (currentId === winnerId) return;

      if (existing) {
        await supabase.from("matches").update(payload).eq("id", existing.id);
      } else {
        await supabase.from("matches").insert([
          {
            ...payload,
            bracket_slot_id: nextSlot,
            group_name: "Završnica",
            is_completed: false,
            scorea: 0,
            scoreb: 0,
            match_day: "", // Prazno, rješavaš u AdminRaspored
            match_time: "", // Prazno
            events: [],
            rostera: [],
            rosterb: [],
          },
        ]);
      }
      fetchInitialData();
    },
    [matches, fetchInitialData],
  );

  useEffect(() => {
    const advance = async () => {
      for (const m of matches) {
        if (m.is_completed && m.scorea !== m.scoreb) {
          const winnerId = m.scorea > m.scoreb ? m.team_a_id : m.team_b_id;
          const prog = progressionMap[m.bracket_slot_id];
          if (prog && winnerId) {
            await updateNextRound(prog.nextSlot, prog.side, winnerId);
          }
        }
      }
    };
    if (matches.length > 0) advance();
  }, [matches, updateNextRound]);

  // RUČNI ODABIR - SADA BEZ AUTOMATSKOG VREMENA
  const handleTeamSelect = async (slotId, side, teamId) => {
    const validId = teamId === "" ? null : teamId;
    const existing = matches.find((m) => m.bracket_slot_id === slotId);
    const payload =
      side === "t1" ? { team_a_id: validId } : { team_b_id: validId };

    if (existing) {
      await supabase.from("matches").update(payload).eq("id", existing.id);
    } else {
      await supabase.from("matches").insert([
        {
          ...payload,
          bracket_slot_id: slotId,
          group_name: "Završnica",
          match_time: "", // VIŠE SE NE STAVLJA AUTOMATSKI
          match_day: "", // VIŠE SE NE STAVLJA AUTOMATSKI
          is_completed: false,
          scorea: 0,
          scoreb: 0,
          events: [],
          rostera: [],
          rosterb: [],
        },
      ]);
    }
    fetchInitialData();
  };

  const getM = (id) => matches.find((m) => m.bracket_slot_id === id) || {};
  const isTeamDisabled = (tid) =>
    matches.some(
      (m) =>
        (m.team_a_id === tid || m.team_b_id === tid) && m.bracket_slot_id <= 8,
    );

  if (loading) return <LoaderBox text="Učitavanje..." />;

  return (
    <AdminChampionshipBracket
      slotData={slotData}
      thirdPlaceSlot={16}
      filteredTeams={filteredTeams}
      getM={getM}
      handleTeamSelect={handleTeamSelect}
      isTeamDisabled={isTeamDisabled}
      stageIcon={stageIcon}
    />
  );
};

export default AdminChampionship;
